// ----------------------------------------------------------------------
// Quiz d'orientation — single source of truth for the question flow.
//
// The whole quiz is described here as data so the UI never hard-codes a
// question: to add a domain, a sub-domain or reword a branch you only edit
// this file. The engine hook (hooks/useQuiz) walks QUIZ_QUESTIONS in order,
// showing a question only when its `when` predicate passes and resolving its
// title/options from the answers already given.
//
// Flow (owner spec):
//   Q1  cible          — pour qui ?
//   Q1b profil         — vous êtes ?           (branches on Q1)
//   Q2  domaine        — quel domaine ?
//   Q2b sous_domaine   — précision            (only domains with a catalog)
//   Q3+Q4 maturite     — où en êtes-vous ?     (labels adapt to the sub-domain,
//                                               generic fallback = old Q4)
//   Q5  motivation     — pourquoi ? (+ champ libre optionnel)
//   Q6  delai          — quand ?
//
// V1 covers "marketing" end-to-end; other domains stop after Q2
// ("Arrêt si formation recherchée autre que marketing"). Widen coverage by
// adding to SOUS_DOMAINES / MATURITE_BY_SOUS_DOMAINE and AVAILABLE_DOMAINES.
// ----------------------------------------------------------------------

import type { QuizAnswers, QuizOption, QuizQuestion, QuizResolvable } from "@/types/quiz";

// ---- Q1 — pour qui recherchez-vous une formation ? -------------------
const CIBLE_OPTIONS: QuizOption[] = [
  { value: "individuel", label: "Pour moi-même (indépendant, salarié, recherche d'emploi...)" },
  { value: "entreprise", label: "Pour mon entreprise / mon équipe" },
];

// ---- Q1bis — vous êtes ? (branches on Q1) ----------------------------
const PROFIL_INDIVIDUEL_OPTIONS: QuizOption[] = [
  { value: "salarie", label: "Salarié(e)" },
  { value: "independant", label: "Indépendant(e) / freelance / chef d'entreprise individuelle" },
  { value: "recherche_emploi", label: "En recherche d'emploi" },
  { value: "reconversion", label: "En reconversion professionnelle" },
  { value: "etudiant", label: "Étudiant(e) / en formation initiale" },
  { value: "autre", label: "Autre" },
];

const PROFIL_ENTREPRISE_OPTIONS: QuizOption[] = [
  { value: "dirigeant", label: "Dirigeant(e) / gérant(e)" },
  { value: "rh_formation", label: "Responsable RH / formation" },
  { value: "manager", label: "Manager / responsable d'équipe" },
  { value: "salarie_mandate", label: "Salarié(e) mandaté(e) pour ce projet" },
  { value: "autre", label: "Autre" },
];

// ---- Q2 — quel domaine de formation ? --------------------------------
export const DOMAINE_OPTIONS: QuizOption[] = [
  { value: "marketing", label: "Marketing, communication & vente" },
  { value: "management", label: "Management & leadership" },
  { value: "rh", label: "Ressources humaines & droit social" },
  { value: "it_data", label: "Informatique, digital & data" },
  { value: "finance", label: "Finance, comptabilité & gestion" },
  { value: "langues", label: "Langues" },
  { value: "dev_perso", label: "Développement personnel & soft skills" },
  { value: "creation_entreprise", label: "Création & gestion d'entreprise" },
  { value: "technique", label: "Technique, industrie & métiers spécifiques" },
  { value: "qhse", label: "Santé, sécurité & qualité (QHSE)" },
  { value: "juridique", label: "Juridique & réglementaire" },
  { value: "bureautique", label: "Bureautique & outils numériques" },
  { value: "autre", label: "Autre / je ne sais pas encore" },
];

// ---- Q2bis — sous-domaines, keyed by domaine (marketing only in V1) ---
export const SOUS_DOMAINES: Record<string, QuizOption[]> = {
  marketing: [
    { value: "reseaux_sociaux", label: "Réseaux sociaux" },
    { value: "seo_sea", label: "SEO / SEA & acquisition de trafic" },
    { value: "strategie_digitale", label: "Stratégie digitale & pilotage marketing" },
    { value: "content_marketing", label: "Content marketing & création de contenu" },
    { value: "email_marketing", label: "Email marketing & marketing automation" },
    { value: "vente_prospection", label: "Vente & prospection commerciale" },
    { value: "ne_sais_pas", label: "Je ne sais pas encore" },
  ],
};

// ---- Q3 + Q4 — maturité (3 niveaux, libellés adaptés au sous-domaine) --
// Q3 et Q4 mesurent la même chose : le niveau d'actions déjà en place. Q3
// propose des libellés spécifiques (A/B/C) par sous-domaine, Q4 la version
// générique. On les fusionne en une seule question dont seuls les libellés
// changent — la valeur reste stable (faible/moyenne/forte) pour le scoring.
export type MaturityLevel = "faible" | "moyenne" | "forte";

const MATURITE_LEVELS: MaturityLevel[] = ["faible", "moyenne", "forte"];

/** Sub-domain "I don't know yet" value — treated as no precision for titling. */
const SOUS_DOMAINE_UNSURE = "ne_sais_pas";

/** Libellés génériques (ex-Q4) — repli quand le sous-domaine n'est pas détaillé. */
const MATURITE_FALLBACK: Record<MaturityLevel, string> = {
  faible: "Rien n'est en place aujourd'hui",
  moyenne: "Des actions existent mais sans stratégie ni suivi",
  forte: "Une stratégie est en place, on veut l'optimiser ou former l'équipe",
};

/** Libellés spécifiques (ex-Q3), par sous-domaine marketing. */
const MATURITE_BY_SOUS_DOMAINE: Record<string, Record<MaturityLevel, string>> = {
  reseaux_sociaux: {
    faible: "Pas de présence organisée sur les réseaux",
    moyenne: "Publie régulièrement mais sans stratégie ni suivi",
    forte: "Stratégie éditoriale en place, résultats suivis",
  },
  seo_sea: {
    faible: "Ne sait pas comment son site est référencé",
    moyenne: "Quelques actions SEO/SEA sans méthode ni suivi",
    forte: "Pilote déjà des campagnes, veut optimiser le ROI",
  },
  strategie_digitale: {
    faible: "Pas de plan marketing digital formalisé",
    moyenne: "Actions ponctuelles sans stratégie ni KPI",
    forte: "Stratégie et KPI suivis, veut structurer / former une équipe",
  },
  content_marketing: {
    faible: "Produit peu ou pas de contenu",
    moyenne: "Contenu ponctuel sans ligne éditoriale ni calendrier",
    forte: "Ligne éditoriale en place, veut professionnaliser",
  },
  email_marketing: {
    faible: "N'envoie pas ou très rarement d'emails",
    moyenne: "Envois ponctuels sans scénario ni segmentation",
    forte: "Scénarios automatisés, veut optimiser les performances",
  },
  vente_prospection: {
    faible: "Pas de méthode de prospection définie",
    moyenne: "Process en place mais cycle long / faible transfo",
    forte: "Process rodé, veut structurer une équipe ou closer mieux",
  },
  ne_sais_pas: {
    faible: "Découvre le marketing digital",
    moyenne: "S'en sert déjà mais veut structurer",
    forte: "Maîtrise les bases, veut se perfectionner",
  },
};

const resolveMaturiteOptions = (answers: QuizAnswers): QuizOption[] => {
  const matrix = MATURITE_BY_SOUS_DOMAINE[answers.sous_domaine] ?? MATURITE_FALLBACK;
  return MATURITE_LEVELS.map((level) => ({ value: level, label: matrix[level] }));
};

// ---- Q5 — pourquoi ? -------------------------------------------------
const MOTIVATION_OPTIONS: QuizOption[] = [
  { value: "probleme_precis", label: "J'ai une problématique précise à résoudre" },
  { value: "obligation_legale", label: "J'ai une obligation légale ou réglementaire" },
  { value: "evolution_poste", label: "Je change de poste / je prends de nouvelles responsabilités" },
  { value: "projet_equipe", label: "Je veux faire monter en compétence mon équipe / accompagner un projet d'entreprise" },
  { value: "envie_progresser", label: "J'ai simplement envie de progresser / me tenir à jour" },
];

// ---- Q6 — quand ? ----------------------------------------------------
const DELAI_OPTIONS: QuizOption[] = [
  { value: "urgent", label: "Le plus vite possible (moins d'un mois)" },
  { value: "3_mois", label: "Dans les 3 mois" },
  { value: "sans_delai", label: "Je me renseigne, pas de délai précis" },
];

// ---- Helpers ---------------------------------------------------------

/** Human label for a domaine value, e.g. "marketing" → "Marketing, communication & vente". */
export const getDomaineLabel = (domaine: string): string =>
  DOMAINE_OPTIONS.find((o) => o.value === domaine)?.label ?? "";

/** Human label for a sous-domaine value within its domaine. */
export const getSousDomaineLabel = (domaine: string, sousDomaine: string): string =>
  (SOUS_DOMAINES[domaine] ?? []).find((o) => o.value === sousDomaine)?.label ?? "";

/**
 * Domains fully built out in this version. Others stop right after Q2
 * ("Arrêt si formation recherchée autre que marketing"). Add a value here
 * once its sub-domain/maturité content exists to open the rest of the flow.
 */
export const AVAILABLE_DOMAINES = new Set<string>(["marketing"]);

export const isDomaineAvailable = (domaine: string): boolean =>
  AVAILABLE_DOMAINES.has(domaine);

// ---- The flow --------------------------------------------------------
export const QUIZ_QUESTIONS: QuizQuestion[] = [
  // Q1
  {
    id: "cible",
    title: "Pour qui recherchez-vous une formation ?",
    options: CIBLE_OPTIONS,
  },
  // Q1bis — branches on Q1
  {
    id: "profil",
    title: "Vous êtes ?",
    objective:
      "Qualifier le répondant (situation, décideur ou relais) pour adapter le ton du résultat et le suivi commercial.",
    options: (a) =>
      a.cible === "entreprise" ? PROFIL_ENTREPRISE_OPTIONS : PROFIL_INDIVIDUEL_OPTIONS,
    when: (a) => Boolean(a.cible),
  },
  // Q2
  {
    id: "domaine",
    title: "Quel domaine de formation recherchez-vous ?",
    options: DOMAINE_OPTIONS,
  },
  // Q2bis — only for domains that expose a sub-domain catalog (marketing in V1)
  {
    id: "sous_domaine",
    title: (a) => `Avez-vous une idée plus précise en ${getDomaineLabel(a.domaine)} ?`,
    options: (a) => SOUS_DOMAINES[a.domaine] ?? [],
    when: (a) => Boolean(SOUS_DOMAINES[a.domaine]?.length),
  },
  // Q3 + Q4 (merged) — maturité; labels adapt to the sub-domain, generic fallback
  {
    id: "maturite",
    title: (a) => {
      const precise =
        a.sous_domaine && a.sous_domaine !== SOUS_DOMAINE_UNSURE
          ? getSousDomaineLabel(a.domaine, a.sous_domaine)
          : "";
      const scope = precise || getDomaineLabel(a.domaine);
      return scope ? `Où en êtes-vous aujourd'hui en ${scope} ?` : "Où en êtes-vous aujourd'hui ?";
    },
    options: resolveMaturiteOptions,
    when: (a) => isDomaineAvailable(a.domaine),
  },
  // Q5 — motivation (+ champ libre non obligatoire)
  {
    id: "motivation",
    title: "Pourquoi avez-vous besoin d'une formation ?",
    objective:
      "Comprendre ce qui motive la demande maintenant pour orienter le message du résultat et prioriser le lead.",
    options: MOTIVATION_OPTIONS,
    freeText: {
      id: "motivation_precisions",
      placeholder: "Précisez votre besoin si vous le souhaitez (facultatif)",
    },
    when: (a) => isDomaineAvailable(a.domaine),
  },
  // Q6 — délai
  {
    id: "delai",
    title: "Quand souhaitez-vous suivre cette formation ?",
    options: DELAI_OPTIONS,
    when: (a) => isDomaineAvailable(a.domaine),
  },
];

// ---- Submission summary ---------------------------------------------

/** A human-readable question/answer pair, ready to submit and display. */
export interface QuizSummaryItem {
  key: string;
  question: string;
  answer: string;
}

/** Resolve a static-or-derived config value against the current answers. */
const resolveValue = <T,>(value: QuizResolvable<T>, answers: QuizAnswers): T =>
  typeof value === "function" ? (value as (a: QuizAnswers) => T)(answers) : value;

/**
 * Walk the visible path for the given answers and produce ordered, labelled
 * question/answer pairs. This keeps the human-readable form of the quiz in one
 * place (the config), so the submission payload and the notification e-mails
 * never re-implement the label lookups.
 */
export function buildQuizSummary(answers: QuizAnswers): QuizSummaryItem[] {
  const items: QuizSummaryItem[] = [];

  for (const question of QUIZ_QUESTIONS) {
    if (question.when && !question.when(answers)) continue;

    const value = answers[question.id];
    if (value) {
      const options = resolveValue(question.options, answers);
      const answer = options.find((o) => o.value === value)?.label ?? value;
      items.push({
        key: question.id,
        question: resolveValue(question.title, answers),
        answer,
      });
    }

    // Optional free-text companion (e.g. Q5 "précisions").
    if (question.freeText) {
      const freeValue = answers[question.freeText.id];
      if (freeValue) {
        items.push({ key: question.freeText.id, question: "Précisions", answer: freeValue });
      }
    }
  }

  return items;
}
